// Per-page SEO composable for the redesigned marketing site.
//
// Applies a `PageSeo` model to the document head so every page can declare a
// unique `<title>` and meta description (Requirement 10.1), and the Home page
// can additionally publish Open Graph social-sharing tags (Requirement 10.2).
//
// The PageSeo model (see design.md → "SEO Metadata Model"):
//   {
//     title:       string,  // unique per page (Req 10.1)
//     description: string,  // unique per page (Req 10.1)
//     og?: { title: string, description: string, image: string } // Home (Req 10.2)
//   }
//
// The composable is SSR/test-safe: it guards every access to `document`, so it
// degrades to a no-op in environments where the DOM is unavailable and never
// throws. It is reactive — pass a ref, getter, or plain object and the head is
// updated whenever the source changes.

import { watch, onScopeDispose, toValue } from 'vue'

// Open Graph property keys driven from the PageSeo `og` block.
const OG_KEYS = ['title', 'description', 'image']

// Detect whether the DOM is available in the current environment.
function canUseDom () {
  return typeof document !== 'undefined' && document !== null
}

// Find or create a <meta> element identified by an attribute/value pair
// (e.g. name="description" or property="og:title") and set its content.
function upsertMeta (attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`)
  if (!el) {
    el = document.createElement('meta')
    el.setAttribute(attr, key)
    document.head.appendChild(el)
  }
  el.setAttribute('content', content)
  return el
}

// Apply a single resolved PageSeo object to the document head.
function applySeo (seo) {
  if (!canUseDom() || !seo || typeof seo !== 'object') {
    return
  }

  if (typeof seo.title === 'string' && seo.title.length > 0) {
    document.title = seo.title
  }

  if (typeof seo.description === 'string') {
    upsertMeta('name', 'description', seo.description)
  }

  // Open Graph tags are optional and used by the Home page (Requirement 10.2).
  if (seo.og && typeof seo.og === 'object') {
    OG_KEYS.forEach((key) => {
      const value = seo.og[key]
      if (typeof value === 'string') {
        upsertMeta('property', `og:${key}`, value)
      }
    })
  }
}

/**
 * Apply a PageSeo model to the document head, reactively.
 *
 * @param {object|import('vue').Ref<object>|(() => object)} source
 *   The PageSeo model, or a ref/getter resolving to one.
 * @returns {{ apply: () => void }} `apply` forces a re-application on demand.
 */
export function useSeo (source) {
  const apply = () => applySeo(toValue(source))

  // Apply immediately and whenever the resolved PageSeo changes.
  watch(
    () => toValue(source),
    apply,
    { immediate: true, deep: true }
  )

  // No teardown is required: the next page that mounts overwrites the title and
  // meta tags with its own PageSeo. onScopeDispose is kept as a safe hook point
  // and is a no-op outside a component/effect scope.
  onScopeDispose(() => {})

  return { apply }
}

export default useSeo
